/**
 * CareTeamCombobox: a text box for a field that names someone who is, or
 * should be, on the client's care team, with a list of the client's care
 * team first and the Directory after it.
 *
 * The text is the field's value. A keystroke drops any pick, so a picked
 * name that is then edited is a typed name; under two characters (after
 * trim) the list is closed. At two or more the list opens on the client's
 * active care-team rows whose name holds the text, in the record's order,
 * and, after a short wait, the Directory's answer to the text as it
 * stands. A Directory entry a listed care-team row already links is left
 * out, since that row stands for it. While the Directory has not answered
 * the list says so; with no option in either group once it has, the one
 * no-match line says the name is saved as typed. Neither line is a
 * choice. Every answered list ends with the Directory's add or request
 * door (DirectoryAddDoor), match or not (LP-26: a name the Directory
 * nearly knows, "Dr." matching a dozen, must still be addable); a listing
 * added there is picked as a Directory option is.
 *
 * The box holds the focus throughout. The first option is active when the
 * list opens; ArrowDown and ArrowUp move the active option and wrap; Enter
 * picks it; a click picks, its mousedown keeping the focus in the box.
 * Escape with the list open closes the list and stops there, keeping the
 * text; with the list closed it keeps the text and reaches the dialog.
 * Tab and leaving the box close the list and keep the text, except that
 * Tab with the door on offer (an answered list) moves to it and keeps the
 * list open.
 *
 * The box is the house search box (SearchField): the leading search
 * icon, the placeholder "Search by name" beside the field's own label,
 * and the clear button once there is text, which empties the box and
 * drops any pick. A typed name takes its name case on leaving the box; a
 * picked name is left as its source has it.
 *
 * A pick sets the text to the option's name and hands the host what it
 * names: a care-team row, or a Directory entry. The line under the box
 * says where a pick came from; a typed name has no line. The box never
 * fetches the client record and never sends anything.
 */

import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { orderedCareProviders } from '@/lib/careProviders';
import type { ClientPronouns, ProviderRow } from '@/lib/clientRecord';
import { DIRECTORY_SEARCH_MIN_CHARS, directoryResultLine, useDirectorySearch } from '@/lib/directorySearch';
import { pronounForms } from '@/lib/pronounForms';
import { nameCase, type NameCaseKind } from '@/lib/valueCase';
import { SearchField } from '@/components/primitives/SearchField';
import { DIRECTORY_LINKED, DIRECTORY_NO_MATCH } from '@/components/clientRecord/DirectoryNameBox';
import { DirectoryAddDoor, useDirectoryDoorSeat } from '@/components/clientRecord/DirectoryAddDoor';

const MIN_CHARS = DIRECTORY_SEARCH_MIN_CHARS;
const LIMIT = 20;
/** The house search placeholder; the field's label stays beside it. */
const PLACEHOLDER = 'Search by name';

/** What a picked name stands for. */
export type CareTeamPick =
  | { kind: 'care_team'; providerId: string }
  | { kind: 'directory'; type: 'individual' | 'organization'; id: string };

export interface CareTeamComboboxProps {
  /** The input's id (the Field's htmlFor); option ids derive from it. */
  id: string;
  /** The text in the box. */
  value: string;
  /** What the text names, when it was picked. */
  pick: CareTeamPick | null;
  onChange(next: { value: string; pick: CareTeamPick | null }): void;
  /** The client's care team; only its active rows are listed. */
  careTeam: readonly ProviderRow[];
  /** The client's pronouns, for "On her care team."; null reads "this client's". */
  pronouns: ClientPronouns | null;
  /** The listbox's accessible name. */
  listLabel: string;
  /** The box's data-testid; the options and the line under the box derive theirs. */
  testIdPrefix: string;
  describedBy?: string;
  invalid?: boolean;
  /** The field must hold a name (aria-required). */
  required?: boolean;
  /** Only Directory organizations, and only care-team rows linked to
   *  one (a hospital or rehab facility). */
  only?: 'organization';
  /** The name case a typed name takes on leaving the box ('person' when
   *  absent; a facility is a 'place'). */
  nameKind?: NameCaseKind;
}

interface Option {
  key: string;
  group: 'team' | 'directory';
  name: string;
  detail: string;
  pick: CareTeamPick;
}

function kindWord(type: 'individual' | 'organization'): string {
  return type === 'individual' ? 'Person' : 'Organization';
}

/** The Directory entry a care-team row links, as a search result's type
 *  and id, or null for a row with no Directory link. */
function linkedEntry(row: ProviderRow): { type: 'individual' | 'organization'; id: string } | null {
  if (row.link.kind === 'individual') return { type: 'individual', id: row.link.id };
  if (row.link.kind === 'org') return { type: 'organization', id: row.link.id };
  return null;
}

/** A care-team row's second line: on the care team, then, for a linked
 *  row, its kind and details. */
function teamDetail(row: ProviderRow, onTeam: string): string {
  const entry = linkedEntry(row);
  if (entry === null) return onTeam;
  const parts = [kindWord(entry.type), row.specialty, row.organization].filter(
    (p): p is string => typeof p === 'string' && p.trim() !== '',
  );
  return `${onTeam} ${parts.join(', ')}`;
}

export function CareTeamCombobox({
  id,
  value,
  pick,
  onChange,
  careTeam,
  pronouns,
  listLabel,
  testIdPrefix,
  describedBy,
  invalid,
  required,
  only,
  nameKind = 'person',
}: CareTeamComboboxProps): ReactNode {
  const listboxId = useId();
  const statusId = `${id}-status`;
  // Open from a keystroke that leaves enough text, until a pick, Escape,
  // Tab or leaving the box closes it.
  const [open, setOpen] = useState(false);
  // The active option's index; the first when the list opens.
  const [active, setActive] = useState(0);
  // A keystroke since the box took the focus: only a typed name is
  // name-cased on leaving it.
  const typed = useRef(false);

  const q = value.trim();
  const expanded = open && q.length >= MIN_CHARS;
  const directory = useDirectorySearch({ q, enabled: expanded, limit: LIMIT, ...(only !== undefined ? { type: only } : {}) });
  const pending = directory.loading;
  const onTeam = `On ${pronounForms(pronouns).possessive} care team.`;

  const needle = q.toLowerCase();
  // The care team's order everywhere: not in the Directory, then people,
  // then organizations, each A to Z.
  const teamRows = expanded
    ? orderedCareProviders(careTeam).filter(
        (row) =>
          row.displayName.toLowerCase().includes(needle) && (only === undefined || linkedEntry(row)?.type === only),
      )
    : [];
  const teamOptions: Option[] = teamRows.map((row) => ({
    key: `team-${row.id}`,
    group: 'team',
    name: row.displayName,
    detail: teamDetail(row, onTeam),
    pick: { kind: 'care_team', providerId: row.id },
  }));
  const listedEntries = teamRows.map(linkedEntry).filter((e) => e !== null);
  const directoryRows = directory.rows;
  const directoryOptions: Option[] = directoryRows
    .filter((r) => only === undefined || r.type === only)
    .filter((r) => !listedEntries.some((e) => e.type === r.type && e.id === r.id))
    .map((r) => ({
      key: `directory-${r.type}-${r.id}`,
      group: 'directory',
      name: r.display_name,
      // Who the entry is (kind or specialty, practice, town); an entry
      // with none of those reads its kind.
      detail: directoryResultLine(r) || kindWord(r.type),
      pick: { kind: 'directory', type: r.type, id: r.id },
    }));
  const options = [...teamOptions, ...directoryOptions];
  // The list has its answer: the door ends it, match or not.
  const settled = expanded && !pending;
  // Tab moves to the door only where the door is drawn.
  const doorSeat = useDirectoryDoorSeat();
  const doorOffered = doorSeat && settled && q !== '';

  const optionId = (index: number): string => `${id}-o${index}`;
  const activeIndex = options.length === 0 ? -1 : Math.min(active, options.length - 1);
  const activeOption = activeIndex === -1 ? undefined : options[activeIndex];
  const activeId = expanded && activeIndex !== -1 ? optionId(activeIndex) : undefined;

  // Keep the active option in view (where the method exists).
  useEffect(() => {
    if (activeId === undefined) return;
    const el = document.getElementById(activeId);
    if (el !== null && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  function choose(option: Option): void {
    setOpen(false);
    onChange({ value: option.name, pick: option.pick });
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!expanded || options.length === 0) return;
      e.preventDefault();
      const n = options.length;
      const step = e.key === 'ArrowDown' ? 1 : n - 1;
      setActive((i) => (Math.min(i, n - 1) + step) % n);
      return;
    }
    if (e.key === 'Enter') {
      if (!expanded || activeOption === undefined) return;
      e.preventDefault();
      choose(activeOption);
      return;
    }
    if (e.key === 'Escape') {
      // Prevented, so the search box keeps the text. With the list open
      // the key stops here; with it closed it reaches the dialog.
      e.preventDefault();
      if (expanded) {
        e.stopPropagation();
        setOpen(false);
      }
      return;
    }
    if (e.key === 'Tab' && !doorOffered) setOpen(false);
  }

  const status = pick === null ? null : pick.kind === 'care_team' ? onTeam : DIRECTORY_LINKED;
  const described = [describedBy, status === null ? undefined : statusId].filter(Boolean).join(' ');

  const renderOption = (option: Option, index: number): ReactNode => (
    <li
      key={option.key}
      id={optionId(index)}
      role="option"
      aria-selected={index === activeIndex}
      data-testid={`${testIdPrefix}-option-${option.group}`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => choose(option)}
      className={`cursor-pointer break-words px-2 py-1 hover:bg-sage-50${index === activeIndex ? ' bg-sage-50' : ''}`}
    >
      <span className="block text-sm text-ink">{option.name}</span>
      <span className="block text-xs text-ink-soft">{option.detail}</span>
    </li>
  );

  return (
    <div
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <SearchField
        id={id}
        testId={testIdPrefix}
        value={value}
        placeholder={PLACEHOLDER}
        onChange={(next) => {
          typed.current = next !== '';
          setOpen(next.trim().length >= MIN_CHARS);
          setActive(0);
          onChange({ value: next, pick: null });
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          typed.current = false;
        }}
        onBlur={() => {
          if (!typed.current || pick !== null) return;
          const cased = nameCase(value, nameKind);
          if (cased !== value) onChange({ value: cased, pick: null });
        }}
        role="combobox"
        aria-autocomplete="list"
        aria-controls={expanded ? listboxId : undefined}
        aria-expanded={expanded}
        aria-activedescendant={activeId}
        aria-describedby={described === '' ? undefined : described}
        aria-invalid={invalid === true ? true : undefined}
        aria-required={required === true ? true : undefined}
        autoCapitalize="none"
      />
      {expanded && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label={listLabel}
          className="mt-1 max-h-60 overflow-y-auto rounded-base border border-sage-200 bg-paper"
        >
          {teamOptions.map((option, i) => renderOption(option, i))}
          {pending && <li className="px-2 py-1 text-xs text-sage-400">Searching…</li>}
          {directoryOptions.map((option, i) => renderOption(option, teamOptions.length + i))}
          {!pending && options.length === 0 && (
            <li className="px-2 py-1 text-xs text-sage-400">{DIRECTORY_NO_MATCH}</li>
          )}
        </ul>
      )}
      <DirectoryAddDoor
        query={value}
        settled={settled}
        onAdded={(row) => {
          setOpen(false);
          onChange({ value: row.display_name, pick: { kind: 'directory', type: row.type, id: row.id } });
        }}
        seedType={only ?? (nameKind === 'person' ? 'individual' : 'organization')}
        {...(only !== undefined ? { only } : {})}
        testId={`${testIdPrefix}-door`}
      />
      {status !== null && (
        <p id={statusId} role="status" data-testid={`${testIdPrefix}-status`} className="mt-1 text-xs text-ink-soft">
          {status}
        </p>
      )}
    </div>
  );
}
